import React, { useState, useRef, useMemo, useEffect } from 'react';
import bwipjs from 'bwip-js/browser';
import { Button, Tag, Tabs, TabList, Tab, TabPanels, TabPanel, InlineNotification } from '@carbon/react';
import { Printer, Download, QrCode, Barcode, DataVis_2 } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import {
  extractGs1Data, buildGs1DigitalLinkUrl,
  buildGs1LogisticContentAiString, buildGs1TradeItemAiString, buildGs1SsccAiString,
  normalizeGtin14, normalizeSscc18,
} from '../utils/gs1Utils';

/**
 * ShipmentLabel — displays the shipment EPC as scannable barcodes in multiple formats
 * and provides print-friendly, GS1-compliant labels of three distinct types, per the
 * GS1 Logistic Label Guideline and GS1 General Specifications:
 *
 *   1. PALLET / LOGISTIC LABEL — for the logistic unit (pallet/container) as a whole.
 *      Requires an SSCC. Per [GENSPECS 4.14.1 / 4.14.2]:
 *        - AI (00) SSCC is ALWAYS encoded in its own dedicated GS1-128 symbol, placed
 *          in the bottom third of the label, and is never combined with other AIs.
 *        - AI (01) GTIN and AI (02) CONTENT must never be used together.
 *        - AI (01) GTIN + AI (37) COUNT is FORBIDDEN in the same symbol (mutually
 *          exclusive) — AI (01) is only used when the logistic unit IS a single trade
 *          item (no count).
 *        - AI (02) CONTENT (GTIN of the contained trade items) MAY be combined with
 *          AI (37) COUNT in the SAME symbol — this is the correct way to express
 *          "N cases of GTIN X" on a logistic label.
 *      This component therefore automatically switches between AI (01) alone and
 *      AI (02)+(37) combined depending on whether a quantity is present.
 *
 *   2. PRODUCT / CASE LABEL — for an individual carton/case (trade item) without an
 *      SSCC. Uses AI (01) GTIN + AI (10) LOT + AI (17) EXPIRY in a single GS1-128
 *      symbol. No SSCC, no barcoded count (AI 37 is not applicable to an individual
 *      trade item's own identity).
 *
 *   3. ITEM LABEL — a compact label for small trade items using a GS1 DataMatrix
 *      (2D symbol), which GS1 permits for direct part marking / small item labelling
 *      where linear barcode space is limited. Encodes the same AI (01)(10)(17) data
 *      as the Product Label but in a smaller 2D form factor, alongside an optional
 *      verification QR code.
 *
 * Barcodes are rendered client-side with bwip-js (https://github.com/metafloor/bwip-js)
 * directly onto <canvas> elements — no external barcode-image APIs are used, so the
 * generated barcode always matches the actual shipment data.
 *
 * On-screen preview formats:
 *   - QR Code          — Chillverify verification URL (or GS1 Digital Link as fallback)
 *   - SSCC Barcode     — GS1-128, AI (00) only
 *   - Content Barcode  — GS1-128, AI (01) or AI (02)+(37), per logistic label rules
 *   - GS1 DataMatrix   — 2D matrix combining SSCC + content AIs
 *   - GS1 Digital Link — web-resolvable QR code
 */
const ShipmentLabel = ({ shipment, preview = false }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const printRef = useRef(null);
  const printProductRef = useRef(null);
  const printItemRef = useRef(null);

  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const bgTertiary = isDark ? '#333333' : '#e8e8e8';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  const [barcodeFormat, setBarcodeFormat] = useState('qr');
  const [renderError, setRenderError] = useState(null);
  // Which full label type is shown in on-screen preview mode (set when `preview`).
  const [previewLabel, setPreviewLabel] = useState('pallet');

  // Canvas refs for on-screen preview (one per format so switching tabs doesn't re-render)
  const qrCanvasRef = useRef(null);
  const ssccCanvasRef = useRef(null);
  const contentCanvasRef = useRef(null);
  const datamatrixCanvasRef = useRef(null);
  const digitalLinkCanvasRef = useRef(null);
  // Canvas refs for the hidden Pallet / Logistic Label print template
  const printContentCanvasRef = useRef(null);
  const printSsccCanvasRef = useRef(null);
  // Canvas refs for the hidden Product / Case Label print template (GS1-128, no SSCC)
  const printItemBarcodeCanvasRef = useRef(null);
  // Canvas refs for the hidden Item Label print template (GS1 DataMatrix, compact)
  const printItemDatamatrixCanvasRef = useRef(null);
  const printItemQrCanvasRef = useRef(null);
  // Separate canvas refs for the on-screen full-label preview (when `preview` prop
  // is set). These must be distinct from the print-template refs above, because two
  // DOM nodes cannot share one React ref — only the last-mounted node would win and
  // the other canvas would render blank. Mirroring the print set one-to-one.
  const previewContentCanvasRef = useRef(null);
  const previewSsccCanvasRef = useRef(null);
  const previewItemBarcodeCanvasRef = useRef(null);
  const previewItemDatamatrixCanvasRef = useRef(null);
  const previewItemQrCanvasRef = useRef(null);
  // Canvas ref for the standalone Chillverify verification QR in the preview tab.
  const previewVerifyQrCanvasRef = useRef(null);

  const epcUri = shipment?.epc_uri || '';
  const meta = shipment?.metadata || {};
  const shipmentId = shipment?.id || '';
  const verifyUrl = shipmentId ? `${window.location.origin}/verify/shipment/${shipmentId}` : '';

  // Extract GS1 data from shipment metadata, falling back to parsing the EPC URN
  const gs1Data = useMemo(() => extractGs1Data(shipment), [shipment]);
  const hasGs1Data = !!(gs1Data.gtin || gs1Data.sscc || gs1Data.lot);

  // Human-readable "Tracking #" for the pallet label — the carrier tracking
  // number is the most recognisable shipment reference on a real logistics label,
  // so it is shown as the primary value. Per GS1 Logistics Label practice a
  // reference should be a real supply-chain identifier — never the raw internal
  // database UUID (shipment.id). Fall back through the most meaningful,
  // standards-aligned identifiers available on this shipment:
  //   1. Carrier tracking number (the human-recognised consignment reference)
  //   2. SSCC (AI 00 — the canonical GS1 logistic-unit identifier)
  //   3. The meaningful tail of the EPC URN (e.g. the serial/ref segment)
  // If none exist, this is empty and the line shows "—" on the label.
  const shipmentRef = useMemo(() => {
    if (meta.carrier?.tracking_number) return meta.carrier.tracking_number;
    if (gs1Data.sscc) {
      return `(00) ${String(gs1Data.sscc).replace(/\D/g, '').padStart(18, '0')}`;
    }
    if (epcUri) {
      // Use the last path segment of the URN rather than the raw UUID where
      // possible (e.g. urn:...:sscc:123.4567 → "123.4567").
      const tail = epcUri.split(':').pop();
      return tail || '';
    }
    return '';
  }, [meta.carrier?.tracking_number, gs1Data.sscc, epcUri]);

  // AI (00) — SSCC, encoded ALONE per GS1 Logistic Label rules (never combined with other AIs)
  const gs1SsccAiString = useMemo(
    () => buildGs1SsccAiString(gs1Data),
    [gs1Data],
  );

  // AI (01) or AI (02) + (10) + (17) + (37) — Logistic unit content detail.
  //
  // Per GS1 General Specifications 4.14.1 / 4.14.2 (also GS1 Logistic Label
  // Guideline rules 4-4, 4-5, 4-13, 4-14):
  //   - AI (01) GTIN identifies the logistic unit itself when it IS a single
  //     trade item — AI (37) COUNT must NEVER be combined with AI (01).
  //   - AI (02) CONTENT identifies the GTIN of trade items CONTAINED within
  //     the logistic unit, and MAY be combined with AI (37) COUNT in the same
  //     symbol (e.g. "12 cases of GTIN X"). AI (01) and (02) must never both
  //     appear on the same label.
  // This component therefore uses AI (02)+(37) together whenever a quantity
  // is present, and falls back to AI (01) alone when there is no quantity.
  const gs1ContentAiString = useMemo(
    () => buildGs1LogisticContentAiString(gs1Data),
    [gs1Data],
  );

  const gs1ItemAiString = useMemo(
    () => buildGs1TradeItemAiString(gs1Data),
    [gs1Data],
  );

  const hasQuantity = gs1Data.quantity != null && gs1Data.quantity !== '';
  const formatGtinDisplay = (gtin) => normalizeGtin14(gtin) || '';

  // Full combined AI string for the Pallet Label's GS1 DataMatrix (2D symbols
  // may combine SSCC + content AIs per GS1 General Specifications).
  const gs1AiString = useMemo(
    () => `${gs1SsccAiString}${gs1ContentAiString}`,
    [gs1SsccAiString, gs1ContentAiString]
  );

  const gs1DigitalLink = useMemo(() => buildGs1DigitalLinkUrl(gs1Data), [gs1Data]);

  // Render a single barcode format onto a canvas via bwip-js. Returns true on success.
  const renderBarcode = (canvasEl, format) => {
    if (!canvasEl) return false;
    try {
      if (format === 'qr') {
        const data = verifyUrl || gs1DigitalLink || epcUri;
        if (!data) return false;
        bwipjs.toCanvas(canvasEl, {
          bcid: 'qrcode',
          text: data,
          scale: 4,
          includetext: false,
        });
        return true;
      }
      if (format === 'digital_link') {
        const data = gs1DigitalLink || verifyUrl || epcUri;
        if (!data) return false;
        bwipjs.toCanvas(canvasEl, {
          bcid: 'qrcode',
          text: data,
          scale: 4,
          includetext: false,
        });
        return true;
      }
      if (format === 'sscc_128') {
        // AI (00) only — the dedicated logistic-unit barcode for the bottom panel
        const data = gs1SsccAiString;
        if (!data) return false;
        bwipjs.toCanvas(canvasEl, {
          bcid: 'gs1-128',
          text: data,
          scale: 3,
          height: 14,
          includetext: true,
          textxalign: 'center',
        });
        return true;
      }
      if (format === 'content_128') {
        // AI (01) alone, or AI (02)+(37) combined — the logistic unit's content
        // detail barcode, separate from the SSCC per GS1 Logistic Label standards.
        const data = gs1ContentAiString;
        if (!data) return false;
        bwipjs.toCanvas(canvasEl, {
          bcid: 'gs1-128',
          text: data,
          scale: 3,
          height: 12,
          includetext: true,
          textxalign: 'center',
        });
        return true;
      }
      if (format === 'item_128') {
        // AI (01)(10)(17) — Individual trade item identity for the Product/Case
        // Label. No SSCC, no count (count is not applicable to a single item).
        const data = gs1ItemAiString;
        if (!data) return false;
        bwipjs.toCanvas(canvasEl, {
          bcid: 'gs1-128',
          text: data,
          scale: 3,
          height: 12,
          includetext: true,
          textxalign: 'center',
        });
        return true;
      }
      if (format === 'item_datamatrix') {
        // Compact 2D symbol for the Item Label, encoding the same AI (01)(10)(17)
        // data as the Product Label but in a smaller footprint (GS1 permits
        // GS1 DataMatrix for small trade item / direct part marking use cases).
        const data = gs1ItemAiString;
        if (!data) return false;
        bwipjs.toCanvas(canvasEl, {
          bcid: 'gs1datamatrix',
          text: data,
          scale: 5,
        });
        return true;
      }
      if (format === 'datamatrix') {
        const data = gs1AiString;
        if (!data) return false;
        bwipjs.toCanvas(canvasEl, {
          bcid: 'gs1datamatrix',
          text: data,
          scale: 5,
        });
        return true;
      }
    } catch (err) {
      return err.message || 'Failed to render barcode';
    }
    return false;
  };

  // Re-render on-screen canvases whenever the shipment data or active tab changes
  useEffect(() => {
    setRenderError(null);
    const refMap = {
      qr: qrCanvasRef,
      sscc_128: ssccCanvasRef,
      content_128: contentCanvasRef,
      datamatrix: datamatrixCanvasRef,
      digital_link: digitalLinkCanvasRef,
    };
    const canvasEl = refMap[barcodeFormat]?.current;
    const result = renderBarcode(canvasEl, barcodeFormat);
    if (typeof result === 'string') setRenderError(result);
  }, [barcodeFormat, gs1SsccAiString, gs1ContentAiString, gs1AiString, gs1DigitalLink, verifyUrl, epcUri]);

  // Render the hidden Pallet / Logistic Label print-template canvases.
  // Per GS1 Logistic Label rules, SSCC and content are rendered in separate symbols.
  useEffect(() => {
    if (gs1ContentAiString) {
      renderBarcode(printContentCanvasRef.current, 'content_128');
    }
    if (gs1SsccAiString) {
      renderBarcode(printSsccCanvasRef.current, 'sscc_128');
    }
  }, [gs1SsccAiString, gs1ContentAiString]);

  // Render the hidden Product/Case Label (GS1-128) and Item Label (DataMatrix + QR)
  // print-template canvases whenever the item-level AI string or verification URL changes.
  useEffect(() => {
    if (gs1ItemAiString) {
      renderBarcode(printItemBarcodeCanvasRef.current, 'item_128');
      renderBarcode(printItemDatamatrixCanvasRef.current, 'item_datamatrix');
    }
    if (verifyUrl || gs1DigitalLink || epcUri) {
      renderBarcode(printItemQrCanvasRef.current, 'qr');
    }
  }, [gs1ItemAiString, verifyUrl, gs1DigitalLink, epcUri]);

  // Render the on-screen full-label preview canvases (mirrors the hidden print
  // templates above) so the visible preview shows live barcodes identical to print.
  // Only rendered when the `preview` prop is set, to avoid needless canvas work.
  useEffect(() => {
    if (!preview) return;
    if (gs1ContentAiString) {
      renderBarcode(previewContentCanvasRef.current, 'content_128');
    }
    if (gs1SsccAiString) {
      renderBarcode(previewSsccCanvasRef.current, 'sscc_128');
    }
    if (gs1ItemAiString) {
      renderBarcode(previewItemBarcodeCanvasRef.current, 'item_128');
      renderBarcode(previewItemDatamatrixCanvasRef.current, 'item_datamatrix');
    }
    if (verifyUrl || gs1DigitalLink || epcUri) {
      renderBarcode(previewItemQrCanvasRef.current, 'qr');
      renderBarcode(previewVerifyQrCanvasRef.current, 'qr');
    }
  }, [preview, previewLabel, gs1SsccAiString, gs1ContentAiString, gs1ItemAiString, verifyUrl, gs1DigitalLink, epcUri]);

  const LABEL_PRINT_STYLES = `
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'IBM Plex Sans', Arial, sans-serif;
      padding: 24px;
      background: #fff;
      color: #000;
    }
    .label-container {
      width: 400px;
      margin: 0 auto;
      border: 3px solid #000;
      text-align: center;
      overflow: hidden;
    }
    .label-header-row {
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .label-header {
      font-size: 1.25rem;
      font-weight: 700;
      margin-bottom: 2px;
    }
    .label-subheader {
      font-size: 0.75rem;
      color: #666;
    }
    /* Bordered box grid — mirrors classic GS1 logistics label layout with clear
       visible boundaries between every data section */
    .box-row {
      display: flex;
      border-bottom: 2px solid #000;
    }
    .box-row:last-child { border-bottom: none; }
    .box-half {
      flex: 1;
      padding: 10px 12px;
      text-align: left;
      min-width: 0;
    }
    .box-half + .box-half { border-left: 2px solid #000; }
    .box-full {
      padding: 10px 14px;
      text-align: left;
    }
    .box-label {
      font-size: 0.625rem;
      font-weight: 700;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      color: #000;
      margin-bottom: 6px;
    }
    .box-header {
      padding: 12px 16px;
      text-align: center;
    }
    .addr-block {
      font-size: 0.75rem;
      line-height: 1.5;
    }
    .addr-block strong { font-weight: 700; }
    .info-line {
      font-size: 0.75rem;
      margin: 2px 0;
    }
    .info-line strong { font-weight: 600; }
    .panel-title {
      font-size: 0.6875rem;
      font-weight: 700;
      letter-spacing: 0.05em;
      text-align: left;
      color: #000;
      margin-bottom: 8px;
      border-bottom: 1px solid #ccc;
      padding-bottom: 4px;
    }
    .barcode-img {
      max-width: 100%;
      margin: 8px auto 4px;
      display: block;
    }
    .barcode-content { max-height: 90px; }
    .barcode-sscc { max-height: 140px; }
    .barcode-item { max-height: 90px; }
    .datamatrix-img {
      max-width: 140px;
      margin: 8px auto;
      display: block;
    }
    .qr-img {
      max-width: 100px;
      margin: 8px auto;
      display: block;
    }
    .epc-text {
      font-family: 'Courier New', monospace;
      font-size: 0.6875rem;
      word-break: break-all;
      margin: 8px 0;
      padding: 8px;
      background: #f4f4f4;
    }
    .barcode-caption {
      font-family: 'Courier New', monospace;
      font-size: 0.625rem;
      color: #666;
      margin: 4px 0 4px;
      word-break: break-all;
      text-align: center;
    }
    .sscc-hri {
      font-family: 'Courier New', monospace;
      font-size: 0.875rem;
      font-weight: 700;
      letter-spacing: 0.05em;
      margin-bottom: 4px;
    }
    .box-full.sscc-box {
      background: #fafafa;
      text-align: center;
    }
    .footer {
      padding: 8px 16px 12px;
      font-size: 0.625rem;
      color: #999;
      text-align: center;
    }
    .item-label-container {
      width: 260px;
    }
    .item-label-container .box-full { text-align: center; }
    @media print {
      body { padding: 0; }
      .label-container { border: 3px solid #000; }
    }
  `;

  // Generic print handler: swaps live <canvas> elements in the given hidden
  // template for <img> data-URLs (canvas bitmaps aren't preserved by
  // innerHTML serialization) and writes the result to a new print window.
  const printLabel = (containerRef, title) => {
    const container = containerRef.current;
    if (!container) return;

    const clone = container.cloneNode(true);
    const liveCanvases = container.querySelectorAll('canvas');
    const cloneCanvases = clone.querySelectorAll('canvas');
    liveCanvases.forEach((liveCanvas, i) => {
      const cloneCanvas = cloneCanvases[i];
      if (!cloneCanvas) return;
      const img = document.createElement('img');
      img.className = cloneCanvas.className;
      img.src = liveCanvas.toDataURL('image/png');
      cloneCanvas.replaceWith(img);
    });

    const printContents = clone.innerHTML;
    if (!printContents) return;

    const win = window.open('', '_blank', 'width=600,height=800');
    if (!win) return;

    win.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${title} — ${epcUri}</title>
        <style>${LABEL_PRINT_STYLES}</style>
      </head>
      <body>
        ${printContents}
      </body>
      </html>
    `);
    win.document.close();

    // Wait for images to load before printing
    setTimeout(() => {
      win.focus();
      win.print();
    }, 1000);
  };

  const handlePrintPallet = () => printLabel(printRef, 'Pallet / Logistic Label');
  const handlePrintProduct = () => printLabel(printProductRef, 'Product / Case Label');
  const handlePrintItem = () => printLabel(printItemRef, 'Item Label');

  const handleDownloadImage = () => {
    const refMap = {
      qr: qrCanvasRef,
      sscc_128: ssccCanvasRef,
      content_128: contentCanvasRef,
      datamatrix: datamatrixCanvasRef,
      digital_link: digitalLinkCanvasRef,
    };
    const canvasEl = refMap[barcodeFormat]?.current;
    if (!canvasEl) return;
    canvasEl.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `shipment-label-${shipment?.id || 'chillverify'}-${barcodeFormat}.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, 'image/png');
  };

  // --- Reusable label-template renderers -------------------------------------
  // Each label's markup lives in a single function so the on-screen preview and
  // the hidden print container render byte-for-byte identical labels. The only
  // difference is which set of canvas refs they bind — preview refs vs. print refs
  // (two DOM nodes can never share one React ref).

  const renderPalletLabel = (refs) => (
    <div className="label-container">
      {/* Header */}
      <div className="box-header" style={{ borderBottom: '2px solid #000' }}>
        <div className="label-header">Chillverify</div>
        <div className="label-subheader">Pallet / Logistic Label</div>
      </div>

      {/* Row: FROM | TO */}
      <div className="box-row">
        <div className="box-half">
          <div className="box-label">From</div>
          <div className="addr-block">
            {meta.sender?.name && <>{meta.sender.name}<br /></>}
            {meta.sender?.company && <>{meta.sender.company}<br /></>}
            {meta.sender?.address && <>{meta.sender.address}<br /></>}
            {meta.sender?.city && <>{[meta.sender.city, meta.sender.state, meta.sender.postal_code].filter(Boolean).join(', ')}<br /></>}
            {meta.sender?.country && <>{meta.sender.country}</>}
            {!meta.sender && '—'}
          </div>
        </div>
        <div className="box-half">
          <div className="box-label">To</div>
          <div className="addr-block">
            {meta.receiver?.name && <>{meta.receiver.name}<br /></>}
            {meta.receiver?.company && <>{meta.receiver.company}<br /></>}
            {meta.receiver?.address && <>{meta.receiver.address}<br /></>}
            {meta.receiver?.city && <>{[meta.receiver.city, meta.receiver.state, meta.receiver.postal_code].filter(Boolean).join(', ')}<br /></>}
            {meta.receiver?.country && <>{meta.receiver.country}</>}
            {!meta.receiver && '—'}
          </div>
        </div>
      </div>

      {/* Row: TRACKING/STATUS | CARRIER */}
      <div className="box-row">
        <div className="box-half">
          <div className="box-label">Tracking #</div>
          {shipmentRef ? (
            <p className="info-line" style={{ wordBreak: 'break-all' }}>{shipmentRef}</p>
          ) : (
            <p className="info-line">—</p>
          )}
          <p className="info-line"><strong>Status:</strong> {shipment?.status || '—'}</p>
        </div>
        <div className="box-half">
          <div className="box-label">Carrier</div>
          <p className="info-line">{meta.carrier?.name || '—'}</p>
        </div>
      </div>

      {/* Content box — human-readable AIs + combined GS1-128 barcode */}
      <div className="box-full">
        <div className="box-label">
          Content — {hasQuantity ? 'AI (02) + (37)' : 'AI (01)'}
        </div>
        {meta.product_name && <p className="info-line"><strong>Description:</strong> {meta.product_name}</p>}
        {gs1Data.gtin && (
          <p className="info-line">
            <strong>{hasQuantity ? 'CONTENT (02)' : 'GTIN (01)'}:</strong> {formatGtinDisplay(gs1Data.gtin)}
          </p>
        )}
        {gs1Data.lot && <p className="info-line"><strong>BATCH/LOT (10):</strong> {gs1Data.lot}</p>}
        {gs1Data.expiryDate && <p className="info-line"><strong>USE BY (17):</strong> {new Date(gs1Data.expiryDate).toLocaleDateString()}</p>}
        {hasQuantity && <p className="info-line"><strong>COUNT (37):</strong> {gs1Data.quantity} {meta.quantity_unit || ''}</p>}
        {gs1ContentAiString ? (
          <>
            <canvas className="barcode-img barcode-content" ref={refs.content} />
            <div className="barcode-caption">{gs1ContentAiString}</div>
          </>
        ) : (
          <div className="epc-text">{epcUri}</div>
        )}
      </div>

      {/* SSCC box — the ONLY AI in this barcode, per GS1 Logistic Label rules.
          This is the most prominent barcode on the label and identifies the
          logistic unit (pallet/case), placed in the bottom section. */}
      <div className="box-full sscc-box">
        <div className="box-label">SSCC — Logistic Unit (AI 00)</div>
        <div className="sscc-hri">
          (00) {gs1Data.sscc ? String(gs1Data.sscc).replace(/\D/g, '').padStart(18, '0') : '—'}
        </div>
        {gs1SsccAiString && (
          <canvas className="barcode-img barcode-sscc" ref={refs.sscc} />
        )}
      </div>

      <div className="footer">
        Scan the SSCC or content barcode, or the QR code on the shipment record, to verify cold-chain compliance at chillverify.app
      </div>
    </div>
  );

  const renderProductLabel = (refs) => (
    <div className="label-container">
      <div className="box-header" style={{ borderBottom: '2px solid #000' }}>
        <div className="label-header">Chillverify</div>
        <div className="label-subheader">Product / Case Label</div>
      </div>
      <div className="box-full">
        <div className="box-label">Trade Item — AI (01)</div>
        {meta.product_name && <p className="info-line"><strong>Description:</strong> {meta.product_name}</p>}
        {gs1Data.gtin && <p className="info-line"><strong>GTIN (01):</strong> {formatGtinDisplay(gs1Data.gtin)}</p>}
        {gs1Data.lot && <p className="info-line"><strong>BATCH/LOT (10):</strong> {gs1Data.lot}</p>}
        {gs1Data.expiryDate && <p className="info-line"><strong>USE BY (17):</strong> {new Date(gs1Data.expiryDate).toLocaleDateString()}</p>}
        {hasQuantity && <p className="info-line"><strong>Qty:</strong> {gs1Data.quantity} {meta.quantity_unit || ''}</p>}
        {gs1ItemAiString ? (
          <>
            <canvas className="barcode-img barcode-item" ref={refs.barcode} />
            <div className="barcode-caption">{gs1ItemAiString}</div>
          </>
        ) : (
          <div className="epc-text">{epcUri}</div>
        )}
      </div>
      <div className="footer">
        Scan this barcode to verify product identity at chillverify.app
      </div>
    </div>
  );

  const renderItemLabel = (refs) => (
    <div className="label-container item-label-container">
      <div className="box-header" style={{ borderBottom: '2px solid #000' }}>
        <div className="label-header" style={{ fontSize: '1rem' }}>Chillverify</div>
        <div className="label-subheader">Item Label</div>
      </div>
      <div className="box-full">
        {meta.product_name && <p className="info-line"><strong>{meta.product_name}</strong></p>}
        {gs1Data.gtin && <p className="info-line">GTIN: {formatGtinDisplay(gs1Data.gtin)}</p>}
        {gs1Data.lot && <p className="info-line">LOT: {gs1Data.lot}</p>}
        {gs1Data.expiryDate && <p className="info-line">EXP: {new Date(gs1Data.expiryDate).toLocaleDateString()}</p>}
        {gs1ItemAiString ? (
          <canvas className="datamatrix-img" ref={refs.datamatrix} />
        ) : (
          <div className="epc-text">{epcUri}</div>
        )}
        {(verifyUrl || gs1DigitalLink || epcUri) && (
          <canvas className="qr-img" ref={refs.qr} />
        )}
      </div>
      <div className="footer">Scan to verify at chillverify.app</div>
    </div>
  );

  // Ref bundles: print refs (for the hidden print containers) and preview refs
  // (for the on-screen full-label preview). Same shape, distinct canvas refs.
  const printRefs = {
    pallet: { content: printContentCanvasRef, sscc: printSsccCanvasRef },
    product: { barcode: printItemBarcodeCanvasRef },
    item: { datamatrix: printItemDatamatrixCanvasRef, qr: printItemQrCanvasRef },
  };
  const previewRefs = {
    pallet: { content: previewContentCanvasRef, sscc: previewSsccCanvasRef },
    product: { barcode: previewItemBarcodeCanvasRef },
    item: { datamatrix: previewItemDatamatrixCanvasRef, qr: previewItemQrCanvasRef },
  };

  const LABEL_NAMES = {
    pallet: 'Pallet / Logistic Label',
    product: 'Product / Case Label',
    item: 'Item Label',
    verify: 'Verify QR Code',
  };

  // Standalone Chillverify verification QR card for the preview tab — the QR that
  // scans to the shipment's public verification page (same as the QR Code tab).
  const renderVerifyQrCard = () => (
    <div className="label-container" style={{ width: 260 }}>
      <div className="box-header" style={{ borderBottom: '2px solid #000' }}>
        <div className="label-header" style={{ fontSize: '1rem' }}>Chillverify</div>
        <div className="label-subheader">Verification QR</div>
      </div>
      <div className="box-full" style={{ textAlign: 'center' }}>
        {(verifyUrl || gs1DigitalLink || epcUri) ? (
          <>
            <canvas className="qr-img" style={{ maxWidth: 160 }} ref={previewVerifyQrCanvasRef} />
            <div className="barcode-caption" style={{ wordBreak: 'break-all' }}>
              {verifyUrl || gs1DigitalLink || epcUri}
            </div>
            <p className="info-line" style={{ marginTop: '8px' }}>
              Scans to the Chillverify verification page
            </p>
          </>
        ) : (
          <div className="epc-text">No shipment ID</div>
        )}
      </div>
      <div className="footer">Scan to verify cold-chain compliance at chillverify.app</div>
    </div>
  );

  if (!epcUri && !shipmentId) return null;

  // PREVIEW MODE — render only the selected full label, visibly on screen.
  // The on-screen preview needs the print stylesheet scoped to it so the box
  // grid / barcode sizing matches the printed label exactly.
  if (preview) {
    return (
      <div>
        <style>{LABEL_PRINT_STYLES}</style>
        {/* Label-type selector — click to view a label directly on screen */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
          {['pallet', 'product', 'item', 'verify'].map((key) => (
            <Button
              key={key}
              kind={previewLabel === key ? 'primary' : 'tertiary'}
              size="sm"
              onClick={() => setPreviewLabel(key)}
              data-testid={`view-${key}-label`}
            >
              {LABEL_NAMES[key]}
            </Button>
          ))}
        </div>

        <div style={{
          padding: '16px',
          backgroundColor: bgTertiary,
          borderRadius: 8,
          border: `1px solid ${borderColor}`,
          display: 'flex',
          justifyContent: 'center',
          overflow: 'auto',
        }}>
          {/* White label surface — labels are meant to be read on paper, so we
              always render the label itself on white regardless of app theme. */}
          <div style={{ maxWidth: (previewLabel === 'item' || previewLabel === 'verify') ? 260 : 400, width: '100%' }}>
            {previewLabel === 'pallet' && renderPalletLabel(previewRefs.pallet)}
            {previewLabel === 'product' && renderProductLabel(previewRefs.product)}
            {previewLabel === 'item' && renderItemLabel(previewRefs.item)}
            {previewLabel === 'verify' && renderVerifyQrCard()}
          </div>
        </div>
        <p style={{ fontSize: '0.6875rem', color: textSecondary, marginTop: '8px', textAlign: 'center' }}>
          On-screen preview of the {LABEL_NAMES[previewLabel]}. Use the print buttons on the left to send to a printer.
        </p>
      </div>
    );
  }

  return (
    <div>
      {!hasGs1Data && (
        <div style={{ marginBottom: '12px' }}>
          <InlineNotification
            kind="info"
            title="No GS1 product data"
            subtitle="Add GTIN, lot, and SSCC to this shipment for full GS1-compliant barcodes. Currently showing EPC URI fallback."
            lowContrast
          />
        </div>
      )}

      {renderError && (
        <div style={{ marginBottom: '12px' }}>
          <InlineNotification
            kind="error"
            title="Barcode render error"
            subtitle={renderError}
            lowContrast
          />
        </div>
      )}

      <Tabs>
        <TabList aria-label="Barcode format tabs">
          <Tab
            renderIcon={QrCode}
            onClick={() => setBarcodeFormat('qr')}
            style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
          >
            QR Code
          </Tab>
          <Tab
            renderIcon={Barcode}
            onClick={() => setBarcodeFormat('sscc_128')}
            style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
          >
            SSCC Barcode
          </Tab>
          <Tab
            renderIcon={Barcode}
            onClick={() => setBarcodeFormat('content_128')}
            style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
          >
            Content Barcode
          </Tab>
          <Tab
            renderIcon={DataVis_2}
            onClick={() => setBarcodeFormat('datamatrix')}
            style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
          >
            GS1 DataMatrix
          </Tab>
          <Tab
            renderIcon={QrCode}
            onClick={() => setBarcodeFormat('digital_link')}
            style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
          >
            Digital Link
          </Tab>
        </TabList>

        <TabPanels>
          {/* QR Code — verification URL */}
          <TabPanel>
            <div style={{ padding: '16px 0', textAlign: 'center' }}>
              {verifyUrl || gs1DigitalLink || epcUri ? (
                <canvas
                  ref={qrCanvasRef}
                  style={{
                    maxWidth: 250,
                    borderRadius: 8,
                    backgroundColor: '#fff',
                    border: `1px solid ${borderColor}`,
                  }}
                />
              ) : (
                <div style={{ width: 250, height: 250, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: bgTertiary, borderRadius: 8, border: `1px solid ${borderColor}` }}>
                  <p style={{ color: textSecondary, fontSize: '0.75rem' }}>No shipment ID</p>
                </div>
              )}
              <p style={{ fontSize: '0.75rem', color: textSecondary, marginTop: '8px' }}>
                Scans to Chillverify verification page
              </p>
              <p style={{ fontSize: '0.6875rem', color: textSecondary, fontFamily: 'monospace', marginTop: '4px', wordBreak: 'break-all' }}>
                {verifyUrl || '—'}
              </p>
            </div>
          </TabPanel>

          {/* SSCC Barcode — AI (00) only, per GS1 Logistic Label rules */}
          <TabPanel>
            <div style={{ padding: '16px 0', textAlign: 'center' }}>
              {gs1SsccAiString ? (
                <canvas
                  ref={ssccCanvasRef}
                  style={{
                    maxWidth: '100%',
                    backgroundColor: '#fff',
                    border: `1px solid ${borderColor}`,
                    borderRadius: 8,
                    padding: '8px',
                  }}
                />
              ) : (
                <div style={{ padding: '24px', backgroundColor: bgTertiary, borderRadius: 8, border: `1px solid ${borderColor}` }}>
                  <p style={{ color: textSecondary, fontSize: '0.75rem' }}>No SSCC assigned to this shipment</p>
                </div>
              )}
              <p style={{ fontSize: '0.75rem', color: textSecondary, marginTop: '8px' }}>
                GS1-128 — Logistic Unit Identifier (bottom panel of pallet label)
              </p>
              <p style={{ fontSize: '0.6875rem', color: textSecondary, fontFamily: 'monospace', marginTop: '4px', wordBreak: 'break-all' }}>
                {gs1SsccAiString || '—'}
              </p>
            </div>
          </TabPanel>

          {/* Content Barcode — AI (01) alone, or AI (02)+(37) combined, encoded separately from the SSCC per GS1 Logistic Label rules */}
          <TabPanel>
            <div style={{ padding: '16px 0', textAlign: 'center' }}>
              {gs1ContentAiString ? (
                <canvas
                  ref={contentCanvasRef}
                  style={{
                    maxWidth: '100%',
                    backgroundColor: '#fff',
                    border: `1px solid ${borderColor}`,
                    borderRadius: 8,
                    padding: '8px',
                  }}
                />
              ) : (
                <div style={{ padding: '24px', backgroundColor: bgTertiary, borderRadius: 8, border: `1px solid ${borderColor}` }}>
                  <p style={{ color: textSecondary, fontSize: '0.75rem' }}>No GTIN, lot, or quantity data to encode</p>
                </div>
              )}
              <p style={{ fontSize: '0.75rem', color: textSecondary, marginTop: '8px' }}>
                GS1-128 — {hasQuantity ? 'Content (AI 02) + Count (AI 37), combined' : 'Trade Item Content (AI 01)'}
              </p>
              <p style={{ fontSize: '0.6875rem', color: textSecondary, fontFamily: 'monospace', marginTop: '4px', wordBreak: 'break-all' }}>
                {gs1ContentAiString || '—'}
              </p>
            </div>
          </TabPanel>

          {/* GS1 DataMatrix */}
          <TabPanel>
            <div style={{ padding: '16px 0', textAlign: 'center' }}>
              {gs1AiString ? (
                <canvas
                  ref={datamatrixCanvasRef}
                  style={{
                    maxWidth: 200,
                    backgroundColor: '#fff',
                    border: `1px solid ${borderColor}`,
                    borderRadius: 8,
                  }}
                />
              ) : (
                <div style={{ width: 200, height: 200, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: bgTertiary, borderRadius: 8, border: `1px solid ${borderColor}` }}>
                  <p style={{ color: textSecondary, fontSize: '0.75rem' }}>No GTIN, SSCC, or lot data to encode</p>
                </div>
              )}
              <p style={{ fontSize: '0.75rem', color: textSecondary, marginTop: '8px' }}>
                GS1 DataMatrix with FNC1 separators
              </p>
              <p style={{ fontSize: '0.6875rem', color: textSecondary, fontFamily: 'monospace', marginTop: '4px', wordBreak: 'break-all' }}>
                {gs1AiString || '—'}
              </p>
            </div>
          </TabPanel>

          {/* GS1 Digital Link QR */}
          <TabPanel>
            <div style={{ padding: '16px 0', textAlign: 'center' }}>
              <canvas
                ref={digitalLinkCanvasRef}
                style={{
                  maxWidth: 250,
                  borderRadius: 8,
                  backgroundColor: '#fff',
                  border: `1px solid ${borderColor}`,
                }}
              />
              <p style={{ fontSize: '0.75rem', color: textSecondary, marginTop: '8px' }}>
                GS1 Digital Link — web-resolvable QR
              </p>
              <p style={{ fontSize: '0.6875rem', color: textSecondary, fontFamily: 'monospace', marginTop: '4px', wordBreak: 'break-all' }}>
                {gs1DigitalLink || '—'}
              </p>
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Action buttons — three GS1-compliant label types */}
      <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
        <Button kind="primary" size="sm" renderIcon={Printer} onClick={handlePrintPallet} data-testid="print-label">
          Print Pallet Label
        </Button>
        <Button kind="tertiary" size="sm" renderIcon={Printer} onClick={handlePrintProduct} data-testid="print-product-label">
          Print Product Label
        </Button>
        <Button kind="tertiary" size="sm" renderIcon={Printer} onClick={handlePrintItem} data-testid="print-item-label">
          Print Item Label
        </Button>
        <Button kind="secondary" size="sm" renderIcon={Download} onClick={handleDownloadImage} data-testid="download-barcode">
          Download Image
        </Button>
      </div>
      <p style={{ fontSize: '0.6875rem', color: textSecondary, marginTop: '6px' }}>
        <strong>Pallet Label</strong> — logistic unit (SSCC required) · <strong>Product Label</strong> — carton/case (GS1-128, GTIN only) · <strong>Item Label</strong> — compact GS1 DataMatrix for small trade items
      </p>

      {/* Hidden print template 1 — PALLET / LOGISTIC LABEL, bordered box grid layout
          per the GS1 Logistic Label Guideline (visible boundaries between every
          data section, SSCC in its own dedicated bottom box). */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0 }} ref={printRef}>
        {renderPalletLabel(printRefs.pallet)}
      </div>

      {/* Hidden print template 2 — PRODUCT / CASE LABEL. Trade item (carton/case)
          identity without an SSCC: AI (01) GTIN + AI (10) LOT + AI (17) EXPIRY in
          a single GS1-128 symbol. Count is shown as text only (AI 37 cannot be
          combined with AI 01 on an individual trade item's own barcode). */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0 }} ref={printProductRef}>
        {renderProductLabel(printRefs.product)}
      </div>

      {/* Hidden print template 3 — ITEM LABEL. Compact label for small trade items
          using a GS1 DataMatrix (2D symbol), which GS1 permits where linear
          barcode space is limited (e.g. vials, blister packs, small units). */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0 }} ref={printItemRef}>
        {renderItemLabel(printRefs.item)}
      </div>
    </div>
  );
};

export default ShipmentLabel;
